import { Info, Copy } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import UtilBar from "./UtilBar";

const TYPE_COLORS = {
  trailer_40ft: "#2563EB",
  semi_bed_40ft: "#EF4444",
  truck_32ft: "#22C55E",
};

function colorFor(type) {
  return TYPE_COLORS[type] || "#3B82F6"; // fallback: truck_20ft blue
}

function LoadGroupBadge({ group, index }) {
  if (group === "heavy") {
    return (
      <span
        data-testid={`load-group-heavy-${index}`}
        className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-red-500/40 bg-red-500/10 text-red-300"
      >
        Heavy Load
      </span>
    );
  }
  if (group === "light") {
    return (
      <span
        data-testid={`load-group-light-${index}`}
        className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      >
        Light Load
      </span>
    );
  }
  return null;
}

// Compact "68% wt · 82% floor" chip — surface utilization at a glance.
function UtilizationChip({ wtPct, flPct, index }) {
  // Colour the chip when either dimension is uncomfortably full.
  const worst = Math.max(wtPct, flPct);
  let color = "text-emerald-300 border-emerald-500/30 bg-emerald-500/10";
  if (worst > 95) color = "text-red-300 border-red-500/40 bg-red-500/10";
  else if (worst > 80) color = "text-[#2563EB] border-[#2563EB]/40 bg-[#2563EB]/10";

  return (
    <span
      data-testid={`utilization-chip-${index}`}
      className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${color}`}
      title={`Weight ${wtPct.toFixed(0)}% · Floor ${flPct.toFixed(0)}%`}
    >
      {wtPct.toFixed(0)}% wt · {flPct.toFixed(0)}% floor
    </span>
  );
}

// Info icon → hover tooltip explaining WHY this vehicle was picked.
function WhyThisVehicle({ reason, index }) {
  if (!reason) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={`why-vehicle-btn-${index}`}
            className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-slate-500 hover:text-[#2563EB] hover:border-[#2563EB]/40 transition-colors"
            aria-label="Why this vehicle?"
          >
            <Info className="w-3 h-3" /> Why?
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="end"
          className="max-w-sm bg-[#FFFFFF] text-white/90 border border-[#2563EB]/40 px-3 py-2 shadow-xl"
          data-testid={`why-vehicle-tooltip-${index}`}
        >
          <div className="overline text-[#2563EB] mb-1">Why this vehicle?</div>
          <div className="text-xs leading-relaxed whitespace-pre-wrap">{reason}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// One-tap "copy this truck line" — great for pasting into WhatsApp / email.
function CopyVehicleButton({ alloc, index }) {
  const handleCopy = async () => {
    const wt = `${Math.round(alloc.used_weight_kg)}/${Math.round(alloc.max_payload_kg)} kg`;
    const dims = `${alloc.vehicle_length_cm}×${alloc.vehicle_width_cm}×${alloc.vehicle_height_cm} cm`;
    const line = `Vehicle ${index + 1} · ${alloc.vehicle_name} · ${alloc.loaded_cases.length} cases · ${wt} · ${dims} · Freight: ₹______`;
    try {
      await navigator.clipboard.writeText(line);
      toast.success("Vehicle line copied — paste in WhatsApp / email");
    } catch {
      toast.error("Couldn't access clipboard — long-press to copy manually");
    }
  };
  return (
    <button
      type="button"
      data-testid={`copy-vehicle-btn-${index}`}
      onClick={handleCopy}
      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-slate-500 hover:text-[#2563EB] hover:border-[#2563EB]/40 transition-colors"
      title="Copy this truck line for WhatsApp / email"
    >
      <Copy className="w-3 h-3" /> Copy
    </button>
  );
}

// Indian road-legality badge — "Legal" or "Permit required" (CMVR Rule 93).
function LegalBadge({ status, notes, index }) {
  if (!status) return null;
  const permit = status === "permit_required";
  const cls = permit
    ? "border-amber-400/60 bg-amber-50 text-amber-700"
    : "border-emerald-500/40 bg-emerald-50 text-emerald-700";
  return (
    <span
      data-testid={`legal-badge-${index}`}
      className={`mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${cls}`}
      title={permit ? (notes || []).join("\n") : "Within CMVR Rule 93 road limits"}
    >
      {permit ? "Permit required" : "Legal"}
    </span>
  );
}

function AutoRigBadge({ alloc, index }) {
  if (!alloc.is_synthetic) return null;
  return (
    <span
      data-testid={`auto-rig-badge-${index}`}
      className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-red-400/60 bg-red-50 text-red-700"
      title="No vehicle in your fleet could carry this piece — rig auto-sized by the planner"
    >
      Auto ODC rig{alloc.axle_lines ? ` · ${alloc.axle_lines} axles` : ""}
    </span>
  );
}

export default function VehicleCard({ alloc, index }) {
  const floorArea = (alloc.vehicle_length_cm * alloc.vehicle_width_cm) / 10_000; // m²
  const typeColor = colorFor(alloc.vehicle_type);

  const wtPct = alloc.max_payload_kg > 0 ? (alloc.used_weight_kg / alloc.max_payload_kg) * 100 : 0;
  const flPct = alloc.vehicle_length_cm > 0 ? (alloc.used_length_cm / alloc.vehicle_length_cm) * 100 : 0;

  return (
    <div className="bg-[#F8FAFC] border border-slate-200 fade-up" data-testid={`vehicle-card-${index}`}>
      <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-2 h-2 shrink-0" style={{ background: typeColor }} />
            <div className="overline">Vehicle {index + 1}</div>
            <LoadGroupBadge group={alloc.load_group} index={index} />
            <LegalBadge status={alloc.legal_status} notes={alloc.legal_notes} index={index} />
            <AutoRigBadge alloc={alloc} index={index} />
            <UtilizationChip wtPct={wtPct} flPct={flPct} index={index} />
            <WhyThisVehicle reason={alloc.selection_reason} index={index} />
            <CopyVehicleButton alloc={alloc} index={index} />
          </div>
          <div className="heading text-lg font-semibold mt-1 break-words">{alloc.vehicle_name}</div>
          <div className="mono text-[11px] text-[#64748B] mt-1">
            {alloc.vehicle_length_cm} × {alloc.vehicle_width_cm} × {alloc.vehicle_height_cm} cm
            {" · "}max {alloc.max_payload_kg.toLocaleString()} kg
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="mono text-2xl font-semibold text-[#2563EB]">
            {alloc.loaded_cases.length}
          </div>
          <div className="overline">cases</div>
        </div>
      </div>

      {/* Utilization bars */}
      <div className="p-4 space-y-3 border-b border-slate-200">
        {alloc.legal_status === "permit_required" && (alloc.legal_notes || []).length > 0 && (
          <ul
            data-testid={`legal-notes-${index}`}
            className="mono text-[11px] text-amber-800 bg-amber-50 border border-amber-300 px-3 py-2 space-y-1 list-disc list-inside"
          >
            {alloc.legal_notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        )}
        {alloc.loading_pattern && (
          <div
            data-testid={`loading-pattern-${index}`}
            className="mono text-[11px] text-[#2563EB] bg-[#2563EB]/5 border border-[#2563EB]/20 px-3 py-2 flex items-center gap-2"
          >
            <span className="uppercase tracking-wide text-[10px] opacity-70">Loading pattern</span>
            <span className="font-semibold">{alloc.loading_pattern}</span>
          </div>
        )}
        <UtilBar label="Weight" used={alloc.used_weight_kg} total={alloc.max_payload_kg} unit="kg" />
        <UtilBar label="Length" used={alloc.used_length_cm} total={alloc.vehicle_length_cm} unit="cm" />
        <div className="mono text-[10px] text-[#64748B] flex justify-between pt-1">
          <span>Volume loaded: {alloc.used_volume_m3.toFixed(2)} m³</span>
          <span>Floor: {floorArea.toFixed(2)} m²</span>
        </div>
      </div>

      {/* Cases list */}
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#FFFFFF] sticky top-0">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 overline">#</th>
              <th className="text-left px-3 py-2 overline">Case</th>
              <th className="text-left px-3 py-2 overline">Description</th>
              <th className="text-right px-3 py-2 overline">L×W×H</th>
              <th className="text-right px-3 py-2 overline">kg</th>
            </tr>
          </thead>
          <tbody>
            {alloc.loaded_cases.map((lc, i) => (
              <tr key={`${lc.case.case_no ?? "n"}-${lc.unit_index ?? i}`} className="border-b border-slate-100 hover:bg-white/[0.03]">
                <td className="px-3 py-2 mono text-[#64748B]">{i + 1}</td>
                <td className="px-3 py-2 mono">{lc.case.case_no}</td>
                <td className="px-3 py-2 text-[#E4E4E7] truncate max-w-[220px]">
                  {lc.case.description}
                </td>
                <td className="px-3 py-2 mono text-right text-[#64748B]">
                  {lc.case.length_cm}×{lc.case.width_cm}×{lc.case.height_cm}
                </td>
                <td className="px-3 py-2 mono text-right">
                  {lc.case.weight_kg.toLocaleString()}
                  {lc.case.weight_estimated && (
                    <span className="ml-1 text-[9px] uppercase text-amber-600" title="Estimated from volume @ 250 kg/CBM">est</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
